import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import { customerShape } from 'constants/propTypes';
import { COVID_VOUCHER_REFUND, MARGIN_REFUND } from 'constants/payment';

import { CurrencyInput } from 'components/ui/Inputs';
import ValidationForm from 'components/ui/ValidationForm';

import { ADD_NOTE_TO_BOOKING, ADD_ADDITIONAL_DETAILS } from 'store/mutations';
import { GET_BOOKING } from 'store/queries';
import { useMutation, useApolloClient } from '@apollo/client';
import { findPayoutAndBalanceByRefId } from 'utils/booking';
import SplitPayment from '../../SplitPayment';
import { handleRefundSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import {
  additionalInformationValidator,
  amountValidator,
  reasonCodeValidator,
  getRefundPaymentReferences,
  labelValidator
} from '../../common/WriteOff.utils';

import { WRITE_OFF_REASONS, WRITE_OFF_REASON, reasonCodeToLabel } from '../../../../../constants/reasons';
import formatReason from '../../../../../utils/formatReason';

import { StyledDropdown, StyledTextArea, StyledLabel } from './WriteOffRefund.styles';

export const handleReasonChange = (changeField, resetField) => (event) => {
  const [code, label] = event.target.value.split('_');
  changeField('reasonCode')(code);
  changeField('label')(label);
  resetField('additionalExplanation');
};

const WriteOffRefund = ({
  loading,
  totalPriceToBePaid,
  addPayment,
  addPayoutAndBalance,
  updatePayments,
  customer,
  reference,
  bookingReference,
  currency,
  method,
  intl: { formatMessage },
  isCancelVoucher,
  mainBookingRef,
  isOverpaid = false
}) => {
  const client = useApolloClient();
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);
  const [addAdditionalDetails] = useMutation(ADD_ADDITIONAL_DETAILS);

  return (
    <ValidationForm
      fields={{
        amount: totalPriceToBePaid,
        reasonCode: '',
        additionalInformation: '',
        label: ''
      }}
      validators={{
        amount: (value) => amountValidator(value, totalPriceToBePaid),
        additionalInformation: additionalInformationValidator,
        reasonCode: reasonCodeValidator,
        label: labelValidator
      }}
      validateInitialFields
    >
      {({
        fields: { amount, reasonCode, additionalInformation, label },
        onChange,
        changeField,
        resetField,
        invalidInputs,
        hasInvalidInputs
      }) => (
        <SplitPayment
          title={<FormattedMessage id="payments.AmountToRefundByWriteoff" />}
          subTitle={<FormattedMessage id="refunds.canBeSplit" />}
          label={<FormattedMessage id="refunds.recordRefund" />}
          loading={loading}
          onSubmit={async (e) => {
            e.preventDefault();

            const completeTransaction = amount >= totalPriceToBePaid - MARGIN_REFUND;
            const reason = formatReason(reasonCode, additionalInformation, label);

            await handleRefundSubmit({
              membershipNumber: null,
              completeTransaction,
              paymentAmount: amount,
              totalPriceToBePaid,
              addPayoutAndBalance,
              updatePayments,
              addPayment,
              customer,
              bookingReference,
              reference,
              externalReference: reasonCodeToLabel(reasonCode, label),
              currency,
              method,
              reason,
              isOverpaid
            });

            if (isCancelVoucher) {
              addNote({
                variables: {
                  note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
                  bookingReference: mainBookingRef
                }
              });

              addNote({
                variables: {
                  note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
                  bookingReference
                }
              });
            }

            const {
              data: { booking }
            } = await client.query({
              query: GET_BOOKING,
              variables: {
                reference: bookingReference
              }
            });

            const payoutAndBalancePayments = findPayoutAndBalanceByRefId(reference, booking.payments);
            const refundPaymentReferences = getRefundPaymentReferences({
              reference,
              payoutAndBalancePayments,
              additionalDetails: booking.additionalDetails
            });

            return Promise.all(
              refundPaymentReferences.map((paymentReference) =>
                addAdditionalDetails({
                  variables: {
                    bookingReference,
                    additionalDetails: {
                      paymentRefId: Number(paymentReference),
                      key: WRITE_OFF_REASON,
                      value: reason
                    }
                  }
                })
              )
            );
          }}
          disabled={hasInvalidInputs}
        >
          <CurrencyInput name="amount" value={amount} placeHolder={0} currency={currency} onChange={onChange} />

          <div>
            <StyledLabel>
              <FormattedMessage id="payments.reasonForWriteOff" />
            </StyledLabel>
            <StyledDropdown
              name="reasonCode"
              value={`${reasonCode}_${label}`}
              required
              data-cy="write-off-reason"
              onChange={handleReasonChange(changeField, resetField)}
            >
              <option value="">{formatMessage({ id: 'payments.dropdownDefault' })}</option>
              {WRITE_OFF_REASONS.map(({ code, label }) => (
                <option key={code} value={`${code}_${label}`}>
                  {label}
                </option>
              ))}
            </StyledDropdown>
          </div>

          <div>
            <StyledLabel>
              <FormattedMessage id="payments.writeOffAdditionalInfo" />
            </StyledLabel>
            <StyledTextArea
              name="additionalInformation"
              value={additionalInformation}
              rows="3"
              cols="40"
              data-cy="write-off-refund-additional-information"
              onChange={onChange}
              maxLength={37}
            />
          </div>
          <Errors>
            {invalidInputs.amount && Number(amount) === 0 && <FormattedMessage id="payments.amountZero" />}
            {invalidInputs.amount && Number(amount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
          </Errors>
        </SplitPayment>
      )}
    </ValidationForm>
  );
};

WriteOffRefund.propTypes = {
  bookingReference: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  updatePayments: PropTypes.func,
  customer: customerShape,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  intl: intlShape,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  isOverpaid: PropTypes.bool
};

export default WriteOffRefund;
